import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'
import { notesService } from '@/services/land/notesService'
import type { NoteWithUser } from '@popety-io/types'

interface NotesContextState {
  // Data
  notes: NoteWithUser[]
  filteredNotes: NoteWithUser[]
  loading: boolean
  error: string | null

  // Search
  searchQuery: string
  setSearchQuery: (query: string) => void

  // UI State
  isAddingNote: boolean
  setIsAddingNote: (value: boolean) => void
  editingNoteId: number | null
  setEditingNoteId: (id: number | null) => void
  deleteConfirmId: number | null
  setDeleteConfirmId: (id: number | null) => void

  // Actions
  fetchNotes: (landId: number | string) => Promise<void>
  createNote: (content: string) => Promise<void>
  updateNote: (noteId: number, content: string) => Promise<void>
  deleteNote: (noteId: number) => Promise<void>
  refreshNotes: () => Promise<void>

  // Current land ID
  currentLandId: number | null | string
}

const NotesContext = createContext<NotesContextState | undefined>(undefined)

export const useNotes = () => {
  const context = useContext(NotesContext)
  if (!context) {
    throw new Error('useNotes must be used within a NotesProvider')
  }
  return context
}

interface NotesProviderProps {
  children: React.ReactNode
}

export const NotesProvider: React.FC<NotesProviderProps> = ({ children }) => {
  // Core state
  const [notes, setNotes] = useState<NoteWithUser[]>([])
  const [filteredNotes, setFilteredNotes] = useState<NoteWithUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentLandId, setCurrentLandId] = useState<string | number | null>(
    null
  )

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const searchTimeout = useRef<any>(null)

  // UI state
  const [isAddingNote, setIsAddingNote] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

  // Perform search with debounce
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current)
    }

    searchTimeout.current = setTimeout(() => {
      performSearch(searchQuery)
    }, 300)

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current)
      }
    }
  }, [searchQuery, notes])

  // Helper to extract plain text from HTML for search
  const getTextFromHtml = (html: string) => {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    return tempDiv.textContent || tempDiv.innerText || ''
  }

  const performSearch = (query: string) => {
    if (!query.trim()) {
      setFilteredNotes(notes)
      return
    }

    const queryLower = query.toLowerCase()
    const filtered = notes.filter((note) => {
      // Extract plain text from HTML content for search
      const plainTextContent = getTextFromHtml(note.content)
      const searchText = `${note.authorName} ${plainTextContent}`.toLowerCase()
      return searchText.includes(queryLower)
    })

    setFilteredNotes(filtered)
  }

  // Fetch notes for a specific land
  const fetchNotes = useCallback(async (landId: number | string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentLandId(landId)
      const notes = await notesService.getLandNotes(landId)
      setNotes(notes)
      setFilteredNotes(notes)
    } catch (err) {
      console.error('Error fetching notes:', err)
      setError('Failed to load notes')
      setNotes([])
      setFilteredNotes([])
    } finally {
      setLoading(false)
    }
  }, [])

  // Refresh notes for current land
  const refreshNotes = useCallback(async () => {
    if (currentLandId) {
      await fetchNotes(currentLandId)
    }
  }, [currentLandId, fetchNotes])

  // Create a new note
  const createNote = useCallback(
    async (content: string) => {
      if (!currentLandId || !content.trim()) return

      try {
        const newNote = await notesService.createLandNote(
          String(currentLandId),
          {
            content: content.trim(),
          }
        )

        const updatedNotes = [newNote, ...notes]
        setNotes(updatedNotes)
        setFilteredNotes(updatedNotes)
        setIsAddingNote(false)
      } catch (err) {
        console.error('Error creating note:', err)
        setError('Failed to create note')
        throw err
      }
    },
    [currentLandId, notes]
  )

  // Update an existing note
  const updateNote = useCallback(
    async (noteId: number, content: string) => {
      if (!content.trim()) return

      try {
        const updatedNote = await notesService.updateLandNote(noteId, {
          content: content.trim(),
        })

        const updatedNotes = notes.map((note) =>
          note.id === noteId ? updatedNote : note
        )
        setNotes(updatedNotes)
        setFilteredNotes(updatedNotes)
        setEditingNoteId(null)
      } catch (err) {
        console.error('Error updating note:', err)
        setError('Failed to update note')
        throw err
      }
    },
    [notes]
  )

  // Delete a note
  const deleteNote = useCallback(
    async (noteId: number) => {
      try {
        await notesService.deleteLandNote(noteId)

        const updatedNotes = notes.filter((note) => note.id !== noteId)
        setNotes(updatedNotes)
        setFilteredNotes(updatedNotes)
        setDeleteConfirmId(null)
      } catch (err) {
        console.error('Error deleting note:', err)
        setError('Failed to delete note')
        throw err
      }
    },
    [notes]
  )

  // Reset UI state when land changes
  useEffect(() => {
    setIsAddingNote(false)
    setEditingNoteId(null)
    setDeleteConfirmId(null)
    setSearchQuery('')
  }, [currentLandId])

  const contextValue: NotesContextState = {
    // Data
    notes,
    filteredNotes,
    loading,
    error,

    // Search
    searchQuery,
    setSearchQuery,

    // UI State
    isAddingNote,
    setIsAddingNote,
    editingNoteId,
    setEditingNoteId,
    deleteConfirmId,
    setDeleteConfirmId,

    // Actions
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
    refreshNotes,

    // Current land ID
    currentLandId,
  }

  return (
    <NotesContext.Provider value={contextValue}>
      {children}
    </NotesContext.Provider>
  )
}
